import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PERMISSION, can, formatMinutes, formatMoney, inboxApi, useAuth,
} from '@wa/shared';
import { VolumeChart } from '@/reports/VolumeChart';
import { AgentTable } from '@/reports/AgentTable';
import { TemplateTable } from '@/reports/TemplateTable';
import { Link } from 'react-router-dom';

type Tab = 'overview' | 'agents' | 'templates';

const RANGES = [
  { key: '7', label: '7 days', days: 7 },
  { key: '30', label: '30 days', days: 30 },
  { key: '90', label: '90 days', days: 90 },
];

/**
 * Reports.
 *
 * Everything here reads the nightly rollups, so the numbers are as of last night —
 * which is what a daily chart means anyway. The one exception is the day in
 * progress, and the screen says so rather than letting a half-finished Tuesday look
 * like a bad one.
 */
export function ReportsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const [range, setRange] = useState('30');

  const days = RANGES.find((r) => r.key === range)?.days ?? 30;
  const to = istToday();
  const from = shift(to, -(days - 1));

  const overview = useQuery({
    queryKey: ['report-overview', from, to],
    queryFn: () => inboxApi.reportOverview(from, to),
  });

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'agents', label: 'People' },
    { key: 'templates', label: 'Templates' },
  ];

  const totals = overview.data?.totals;

  return (
    <div className="pane-scroll h-full overflow-y-auto bg-(--color-paper)">
      <div className="@container mx-auto flex max-w-4xl flex-col gap-5 p-3 sm:p-5">
        <header className="flex flex-col gap-3">
          <h1 className="text-title font-semibold tracking-tight">Reports</h1>

          <div role="tablist" aria-label="Reports" className="chip-scroll flex items-center gap-1">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                role="tab"
                aria-selected={tab === item.key}
                onClick={() => setTab(item.key)}
                className={[
                  'tap shrink-0 rounded-(--radius-chip) px-2.5 py-1 text-micro font-medium transition',
                  tab === item.key
                    ? 'bg-(--color-brand) text-(--color-brand-ink)'
                    : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)',
                ].join(' ')}
              >
                {item.label}
              </button>
            ))}

            {/* The range control sits in one row above the charts, and applies to
                every tab — a filter that means something different on each screen is
                a filter people stop trusting. */}
            <label className="ml-auto shrink-0">
              <span className="sr-only">Range</span>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="tap cursor-pointer rounded-(--radius-chip) border border-(--color-line) bg-(--color-surface) px-2 py-1 text-micro text-(--color-muted)"
              >
                {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </label>
          </div>

          {overview.data?.includesToday && (
            <p className="text-micro text-(--color-muted)">
              Figures are rolled up each night, so today is partial and will settle by tomorrow morning.
            </p>
          )}
        </header>

        {tab === 'overview' && (
          <>
            {/* Hero numbers, not charts. Five figures somebody wants at a glance do
                not become clearer for being plotted. */}
            <section className="grid gap-3 @min-[30rem]:grid-cols-3">
              <Figure label="Conversations opened" value={totals?.opened?.toLocaleString('en-IN')} />
              <Figure label="Resolved" value={totals?.resolved?.toLocaleString('en-IN')} />
              <Figure label="Reopened" value={totals?.reopened?.toLocaleString('en-IN')}
                      hint="Closed, then woken by a new message." />
            </section>

            <section className="grid gap-3 @min-[30rem]:grid-cols-2">
              <Figure
                label="First reply by a person"
                value={formatMinutes(totals?.firstResponseOpen)}
                hint={`Working hours. On the clock it is ${formatMinutes(totals?.firstResponseWall)}.`}
                unknown={totals?.unknownResponses}
              />
              <Figure
                label="Time to resolve"
                value={formatMinutes(totals?.resolutionOpen)}
                hint={`Working hours. On the clock it is ${formatMinutes(totals?.resolutionWall)}.`}
                unknown={totals?.unknownResolutions}
              />
            </section>

            <section className="rounded-lg border border-(--color-line) bg-(--color-surface) py-3">
              <h2 className="px-3 pb-2 text-body font-semibold sm:px-4">Messages a day</h2>
              <VolumeChart days={overview.data?.days ?? []} />
            </section>

            <section className="grid gap-3 @min-[30rem]:grid-cols-3">
              <Figure label="Sent" value={totals?.outbound?.toLocaleString('en-IN')} />
              <Figure label="Failed" value={totals?.failed?.toLocaleString('en-IN')} />
              <Figure label="Spent" value={totals ? `₹${formatMoney(totals.spend)}` : undefined} />
            </section>
          </>
        )}

        {tab === 'agents' && <AgentTable from={from} to={to} />}
        {tab === 'templates' && <TemplateTable from={from} to={to} />}

        {can(user, PERMISSION.settingsWrite) && (
          <p className="text-small text-(--color-muted)">
            Response targets &mdash; how fast a reply and a resolution should come &mdash; are set under{' '}
            <Link to="/settings/targets" className="font-medium text-(--color-brand) hover:underline">Settings &rarr; Response targets</Link>.
          </p>
        )}
      </div>
    </div>
  );
}

function Figure({ label, value, hint, unknown }: {
  label: string; value: string | undefined; hint?: string; unknown?: number;
}) {
  return (
    <div className="rounded-lg border border-(--color-line) bg-(--color-surface) px-3 py-2.5">
      <p className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</p>
      <p className="mt-0.5 text-title font-semibold tabular-nums">{value ?? '—'}</p>
      {hint && <p className="mt-0.5 text-micro leading-snug text-(--color-muted)">{hint}</p>}

      {/* Said on the screen somebody reads it on. A figure computed from part of the
          data has to admit which part — conversations resolved before the platform
          started stamping cannot contribute one, and excluding them silently would
          make the average look better than the period was. */}
      {unknown !== undefined && unknown > 0 && (
        <p className="mt-0.5 text-micro leading-snug text-(--color-warn)">
          {unknown.toLocaleString('en-IN')} not counted — no timing was recorded for them.
        </p>
      )}
    </div>
  );
}

/** The IST date, because the rollup's days are IST days. */
function istToday(): string {
  const now = new Date(Date.now() + (5 * 60 + 30) * 60_000);
  return now.toISOString().slice(0, 10);
}

function shift(day: string, byDays: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + byDays);
  return d.toISOString().slice(0, 10);
}
